import { Button } from "@mui/material";
import CustomTable from "components/custom-table/Table";
import { formatDate } from "utils/formatDate";
import DocumentStatus from "./DocumentStatus";
import DocumentActions from "./DocumentActions";
import { useCompanyDocumentsLanguage } from "../i18n/CompanyDocumentsLanguageContext";

export default function DocumentChecklist({
  items,
  onUpload,
  onHistory,
  disabled = false,
}) {
  const { t } = useCompanyDocumentsLanguage();

  return (
    <div className="w-full min-w-0 overflow-x-auto">
      <CustomTable
        showPagination={false}
        headCells={[
          { id: "name", label: t("client.documents.checklist.document") },
          { id: "status", label: t("client.documents.checklist.status") },
          { id: "file", label: t("client.documents.checklist.latestFile") },
          { id: "version", label: t("client.documents.checklist.version") },
          { id: "actions", label: t("client.documents.checklist.actions") },
        ]}
        bodyCells={items.map((item) => ({
          id: item.document_type.id,
          name: (
            <div className="min-w-[160px] max-w-[260px] whitespace-normal">
              <p className="font-semibold break-words">
                {item.document_type.name}
              </p>
              <p className="text-xs text-[#5F737F]">
                {item.requirementLabel ||
                  (item.document_type.is_required
                    ? t(
                        "client.documents.checklist.required",
                      )
                    : t(
                        "client.documents.checklist.optional",
                      ))}
              </p>
            </div>
          ),
          status: <DocumentStatus status={item.status} />,
          file: item.latest ? (
            <div className="min-w-[160px] max-w-[260px] whitespace-normal break-words">
              <p>{item.latest.original_filename}</p>

              {item.latest.review_note && (
                <div className="mt-2 whitespace-pre-wrap break-words">
                  <p className="font-semibold">
                    {t(
                      "client.documents.checklist.siguNote",
                    )}
                  </p>
                  <p>{item.latest.review_note}</p>
                </div>
              )}

              {item.latest.reviewed_at && (
                <p className="text-xs mt-2">
                  {t(
                    "client.documents.checklist.reviewed",
                  )}:{" "}
                  {formatDate.toFullDateTime(
                    item.latest.reviewed_at,
                  )} ·{" "}
                  {item.latest.reviewed_by_name ||
                    t(
                      "client.documents.checklist.admin",
                      {
                        id: item.latest.reviewed_by,
                      },
                    )}
                </p>
              )}

              <p className="text-xs">
                {formatDate.toShortDate(item.latest.uploaded_at)}
              </p>
            </div>
          ) : (
            <span className="text-sm text-[#5F737F]">
              {t(
                "client.documents.checklist.notUploaded",
              )}
            </span>
          ),
          version: item.latest ? `v${item.latest.version}` : "—",
          actions: (
            <div className="grid min-w-[140px] gap-1">
              <Button
                size="small"
                variant="outlined"
                disabled={disabled}
                onClick={() => onUpload(item.document_type)}
              >
                {item.status === "NEED_REVISION"
                  ? t(
                        "client.documents.checklist.uploadNewVersion",
                      )
                  : item.latest
                    ? t(
                          "client.documents.checklist.replace",
                        )
                    : t(
                          "client.documents.checklist.upload",
                        )}
              </Button>

              {item.latest && (
                <>
                  <DocumentActions document={item.latest} />
                  <Button size="small" onClick={() => onHistory(item)}>
                    {t(
                      "client.documents.checklist.viewHistory",
                    )}
                  </Button>
                </>
              )}
            </div>
          ),
        }))}
      />
    </div>
  );
}
